import { InvoiceAgingPlacementRow } from '../gql';

export const DAY = 86_400_000;

// Normalize a Date to UTC midnight to avoid off-by-one near midnight
export function toUtcMidnight(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// Parse a term string (numeric days) — blank/whitespace-only/non-numeric returns null
export function parseDays(term: string | null | undefined): number | null {
  if (term == null) return null;
  const trimmed = term.trim();
  if (trimmed === '') return null;
  const s = Number(trimmed);
  return Number.isFinite(s) ? s : null;
}

// Format a Date using UTC calendar getters (MM/DD/YYYY).
// Use this for all date display in the Invoice Aging table so that UTC-midnight
// dates (both server-supplied and locally computed) render the correct calendar day
// regardless of the browser's local timezone.
export function formatDateUTC(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  const yyyy = dt.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// Invoice Due Date = Start + payableTerm days (null if term blank or no startDate)
export function computeInvoiceDue(row: InvoiceAgingPlacementRow): Date | null {
  if (!row.startDate) return null;
  const pd = parseDays(row.payableTerm);
  if (pd === null) return null;
  const startMs = toUtcMidnight(new Date(row.startDate));
  return new Date(startMs + pd * DAY);
}

// Replacement Date = Start + replacementTerm days; met = today > replacementDate
export function computeReplacement(
  row: InvoiceAgingPlacementRow,
  today: Date
): { date: Date | null; met: boolean } {
  if (!row.startDate) return { date: null, met: false };
  const rd = parseDays(row.replacementTerm);
  if (rd === null) return { date: null, met: false };
  const startMs = toUtcMidnight(new Date(row.startDate));
  const date = new Date(startMs + rd * DAY);
  const todayMs = toUtcMidnight(today);
  const met = todayMs > date.getTime();
  return { date, met };
}

// Aging days = today − Start date (Start = when the client is invoiced, not the
// payment-due date). Only for ACCOUNTING_INVOICED rows. >0 = days outstanding;
// <=0 = not yet started; null = N/A (non-invoiced or missing start date).
export function computeAging(
  row: InvoiceAgingPlacementRow,
  today: Date
): number | null {
  if (row.placementStatus !== 'ACCOUNTING_INVOICED') return null;
  if (!row.startDate) return null;
  const startMs = toUtcMidnight(new Date(row.startDate));
  const todayMs = toUtcMidnight(today);
  return Math.floor((todayMs - startMs) / DAY);
}

// An invoiced row is "past due" only once its aging (days since start) exceeds
// its payable term (days). Below the term it's still within the payment window
// (shown as "Pending"). False for non-invoiced rows, unknown aging, or a
// missing/blank payable term (can't assert overdue without the term).
export function isPastDue(row: InvoiceAgingPlacementRow, today: Date): boolean {
  if (row.placementStatus !== 'ACCOUNTING_INVOICED') return false;
  const aging = computeAging(row, today);
  const payableDays = parseDays(row.payableTerm);
  if (aging === null || payableDays === null) return false;
  return aging > payableDays;
}

export type BucketKey = 'current' | 'b30' | 'b60' | 'b90' | 'b120' | 'b120p';

export function bucketOf(aging: number): BucketKey {
  if (aging <= 0) return 'current';
  if (aging <= 30) return 'b30';
  if (aging <= 60) return 'b60';
  if (aging <= 90) return 'b90';
  if (aging <= 120) return 'b120';
  return 'b120p';
}

export interface SeverityTokens {
  bar: string;
  bg: string;
  fg: string;
}

// VERBATIM from PLAN.md §2.5
export const SEVERITY: Record<BucketKey, SeverityTokens> = {
  current: { bar: '#0fa37f', bg: '#e7f7f1', fg: '#066e52' },
  b30:     { bar: '#e0a423', bg: '#fbeecd', fg: '#946309' },
  b60:     { bar: '#ef8b1e', bg: '#fbe4cb', fg: '#98560a' },
  b90:     { bar: '#ea6a2a', bg: '#fad9c9', fg: '#8a3d12' },
  b120:    { bar: '#e14b30', bg: '#f9d2ca', fg: '#852816' },
  b120p:   { bar: '#cf2318', bg: '#f9d0cc', fg: '#87130b' },
};

// Bar color fallback (when no bucket)
export const PAID_BAR_COLOR = '#cbd0d8';
export const DEFAULT_BAR_COLOR = '#e0a423';

export const BUCKET_LABELS: Record<BucketKey, string> = {
  current: 'Current',
  b30:     '1-30 Days',
  b60:     '31-60 Days',
  b90:     '61-90 Days',
  b120:    '91-120 Days',
  b120p:   '120+ Days',
};

// Row sort rank: invoiced→aging desc, approved→–1000, paid→–2000
export function rowSortRank(
  row: InvoiceAgingPlacementRow,
  today: Date
): number {
  if (row.placementStatus === 'PAID') return -2000;
  if (row.placementStatus === 'ACCOUNTING_APPROVED') return -1000;
  // ACCOUNTING_INVOICED: sort by aging desc (most overdue first)
  const aging = computeAging(row, today);
  return aging !== null ? aging : 0;
}
